/**
 * Shared fetch helper for the booking domain.
 *
 * Two auth realms call the same booking logic through two different route
 * families:
 *   - internal staff: /api/v1/booking/clinics/{client_id}/... (BookingCenter)
 *   - a clinic's own login: /api/client/v1/booking/... - no client_id in the
 *     path at all, because for that realm the tenant id is never something
 *     the caller should be able to name (see client_booking_routes.py).
 * `basePath` is the only thing that differs between them, so every tab
 * component takes it as a prop instead of hardcoding one route family.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export async function bookingRequest(basePath, token, method, path, { params, body } = {}) {
  let url = `${BACKEND_URL}${basePath}${path}`;
  if (params) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ).toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // No JSON body (e.g. a plain 204) - fine, data stays null.
  }

  if (!res.ok) {
    // FastAPI's validation-error detail is an array of {msg, loc, ...}
    // objects, not a string - passed straight to `new Error()` it
    // stringifies to "[object Object]" via Array.prototype.toString,
    // leaving every form on this shared helper unable to show a real
    // validation message (e.g. a field exceeding its max length) even
    // though the backend sent one.
    const detail = data && data.detail;
    const message =
      (Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ") : detail) ||
      (data && data.message) ||
      `Request failed (${res.status})`;
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}
